// inv/src/pages/payment/subscriptionLogic.jsx
import { useState, useEffect, useCallback } from 'react';
import { collectGAContext } from '../../service-scripts/gaContext';
import axios from 'axios';
import { API_URL } from '../../config';
import { useMembershipAccess } from '../../hooks/membership';

export function useSubscriptionLogic() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState({ startup: {}, professional: {} });
  const [selectedInterval, setSelectedInterval] = useState('month');
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const membership = useMembershipAccess({ user, loading });

  const getAuthToken = () => {
    if (typeof document === 'undefined') return null;
    const m = document.cookie.match(/(?:^|;)\s*token=([^;]+)/);
    return m ? m[1] : null;
  };

  const token = getAuthToken();
  const axiosInstance = axios.create({
    baseURL: API_URL,
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    withCredentials: true,
  });

  function groupSubscriptions(products) {
    const result = { startup: {}, professional: {} };
    products.forEach(prod => {
      const typeKey = prod.name.toLowerCase().includes('professional') ? 'professional' : 'startup';
      prod.prices.forEach(price => {
        let period = null;
        const c = price.billing_cycle;
        if (c.interval === 'month' && c.frequency === 1) period = 'month';
        if (c.interval === 'month' && c.frequency === 3) period = 'quarter';
        if (c.interval === 'year' && c.frequency === 1) period = 'year';
        if (!period) return;

        const raw = parseInt(price.unit_price.amount, 10);
        result[typeKey][period] = {
          id: price.id,
          name: price.name || prod.name,
          recurring_price: (raw / 100).toString(),
        };
      });
    });
    return result;
  }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/products`);
        if (data.success) setPlans(groupSubscriptions(data.products));
      } catch (e) {
        console.error('Error fetching plans:', e);
      }
    })();
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/user');

      if (data && data.authenticated === false) {
        setUser(null);
        setLoading(false);
        return;
      }

      setUser(data);
    } catch (e) {
      if (e.response?.status !== 401) {
        console.error('Error fetching user:', e);
      }
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);


  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  useEffect(() => {
    (async () => {
      if (!user?.user_id) return;
      try {
        if (window.gtag && window.GA_MEASUREMENT_ID) {
          window.setGAUserId(user.user_id);
        }

        const { client_id, session_id, session_number, attribution } = await collectGAContext();
        const hasAttribution = attribution && Object.keys(attribution).length > 0;
        if (client_id || hasAttribution) {
          const urlParams = new URLSearchParams(window.location.search);
          const utmData = {
            utm_source: urlParams.get('utm_source') || attribution?.utm_source,
            utm_medium: urlParams.get('utm_medium') || attribution?.utm_medium,
            utm_campaign: urlParams.get('utm_campaign') || attribution?.utm_campaign,
            utm_term: urlParams.get('utm_term') || attribution?.utm_term,
            utm_content: urlParams.get('utm_content') || attribution?.utm_content
          };

          await axiosInstance.post('/ga-context', {
            client_id,
            session_id,
            session_number,
            attribution,
            ...utmData
          });
        }
      } catch (e) {
        console.warn('GA init failed:', e?.response?.data || e.message);
      }
    })();
  }, [user?.user_id]);

  const previewUpgrade = useCallback(
    async ({ priceId, prorationMode = 'prorated_immediately' }) => {
      if (!user?.paddleSubscriptionId) return null;
      if (priceId === user?.lastPriceId || priceId === user?.pendingPriceId) return null;
      try {
        const { data } = await axiosInstance.get(
          `/subscriptions/${user.paddleSubscriptionId}/preview`,
          { params: { priceId, proration: prorationMode } }
        );
        if (data.success) return {
          immediate: data.immediate / 100,
          recurring: data.recurring / 100,
          credit: data.credit / 100,
          balanceCredit: data.balanceCredit / 100,
          currency: data.currency,
          nextBilledAt: data.nextBill ? new Date(data.nextBill) : null,
        };
      } catch (e) {
        console.error(
          'Error previewing upgrade:',
          e.response?.data || e.message
        );
      }
      return null;
    },
    [axiosInstance, user]
  );

  const handlePlanSelect = async ({
    planId,
    prorationMode = 'prorated_immediately',
  }) => {
    if (isUpdating) return null;

    const isSamePrice =
      planId &&
      (planId === user?.lastPriceId || planId === user?.pendingPriceId);

    if (isSamePrice) return null;

    setIsUpdating(true);
    try {
      const body = {
        planId,
        interval: selectedInterval,
        prorationMode,
      };

      const { data } = await axiosInstance.post('/user/membershipupdate', body);

      setUser(data.user);

      const action = data.subscriptionResult?.action;
      if (action === 'checkout') {
        setCheckoutPlanId(planId);
      } else {
        setCheckoutPlanId(null);
      }

      return data.user;
    } catch (e) {
      console.error(
        'Error updating plan or starting checkout:',
        e.response?.data || e.message
      );
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const clearIncompleteCheckout = useCallback(async () => {
    try {
      await axiosInstance.post('/user/clear-incomplete-checkout');
      await fetchUser();
    } catch (e) {
      console.error('Error clearing incomplete checkout:', e);
    }
  }, [axiosInstance, fetchUser]);

  const checkSubscriptionStatus = useCallback(async () => {
    if (!user?.paddleSubscriptionId) return;

    try {
      await axiosInstance.post('/user/sync-subscription-status');
      await fetchUser();
    } catch (e) {
      console.error('Error syncing subscription status:', e);
    }
  }, [axiosInstance, fetchUser, user?.paddleSubscriptionId]);

  const cleanupPendingStates = useCallback(async () => {
    if (!user) return;

    const hasOldPending = membership.hasOldPendingMembership;
    const hasConflictingPending = membership.hasConflictingPendingChange;
    const hasCancelledWithPending = membership.hasCancelledDowngradePending;
    const hasCancelledWithPaidPending = membership.hasCancelledPaidPending;

    if (hasOldPending || hasConflictingPending || hasCancelledWithPending || hasCancelledWithPaidPending) {
      console.log('🧹 Cleaning up old, conflicting, or cancelled pending states');
      await fetchUser();
    }
  }, [membership, user, fetchUser]);

  const fixPendingStates = useCallback(async () => {
    if (!user) return;

    try {
      const { data } = await axiosInstance.post('/user/fix-pending-states');
      if (data.success && data.issues.length > 0) {
        console.log('🔧 Fixed pending states:', data.issues);
        await fetchUser();
      }
    } catch (e) {
      console.error('Error fixing pending states:', e);
    }
  }, [user, axiosInstance, fetchUser]);

  const forceClearPending = useCallback(async () => {
    if (!user) return;

    try {
      const { data } = await axiosInstance.post('/user/force-clear-pending');
      if (data.success && data.hadPending) {
        console.log('🔧 Force cleared pending states');
        await fetchUser();
      }
    } catch (e) {
      console.error('Error force clearing pending states:', e);
    }
  }, [user, axiosInstance, fetchUser]);

  const applyExpiredCancellation = useCallback(async () => {
    if (!user) return;

    try {
      const { data } = await axiosInstance.post('/user/apply-expired-cancellation');
      if (data.success) {
        console.log('🔧 Applied expired cancellation');
        await fetchUser();
      }
    } catch (e) {
      console.error('Error applying expired cancellation:', e);
    }
  }, [user, axiosInstance, fetchUser]);

  const activatePendingSubscription = useCallback(async () => {
    if (!user) return;

    try {
      const { data } = await axiosInstance.post('/user/activate-pending-subscription');
      if (data.success) {
        console.log('🔧 Activated pending subscription');
        await fetchUser();
      }
    } catch (e) {
      console.error('Error activating pending subscription:', e);
    }
  }, [user, axiosInstance, fetchUser]);

  const getSubscriptionStatus = useCallback(async () => {
    if (!user) return null;

    try {
      const { data } = await axiosInstance.get('/user/subscription-status');
      return data.success ? data.status : null;
    } catch (e) {
      console.error('Error getting subscription status:', e);
      return null;
    }
  }, [user, axiosInstance]);

  useEffect(() => {
    if (!user?.paddleSubscriptionId) return;

    const interval = setInterval(() => {
      checkSubscriptionStatus();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user?.paddleSubscriptionId, checkSubscriptionStatus]);

  useEffect(() => {
    if (user) {
      cleanupPendingStates();
      fixPendingStates();
    }
  }, [user, cleanupPendingStates, fixPendingStates]);

  return {
    loading,
    user,
    plans,
    selectedInterval,
    setSelectedInterval,
    checkoutPlanId,
    setCheckoutPlanId,
    handlePlanSelect,
    fetchUser,
    previewUpgrade,
    isUpdating,
    clearIncompleteCheckout,
    checkSubscriptionStatus,
    cleanupPendingStates,
    fixPendingStates,
    forceClearPending,
    applyExpiredCancellation,
    activatePendingSubscription,
    getSubscriptionStatus,
  };
}
